import unescapedText from 'I18n/utils/unescapedText';
import { Children, isValidElement } from 'react';
import devLogger from 'react-utils/devLogger';
import { usePanelContext } from '../../context/PanelContext';
import { DialogCloseButton } from '../DialogCloseButton';
import { DialogHeader } from '../DialogHeader';
import { PanelSection } from '../Panel';
import { Flex } from '../../../components/Flex/Flex';
import { Text } from '../../../components/Text/Text';
import { StepIndicator } from '../StepIndicator';
import { WizardHeaderProvider } from './WizardHeaderProvider';
import styled, { useTheme } from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getStepNames = ({
  wizardProps,
  inPanel
}) => {
  const steps = Children.toArray(wizardProps.children);
  const stepNames = [];
  const stepLimit = inPanel ? 4 : 6;
  let prevStepName = null;
  steps.forEach(step => {
    const stepName = /*#__PURE__*/isValidElement(step) ? step.props.name : null;
    if (process.env.NODE_ENV !== 'production') {
      if (stepName != null && !(typeof stepName === 'string' && stepName.length > 0)) {
        devLogger.warn({
          message: 'WizardHeaderWithOverview: a WizardStep is missing a `name` attribute. Expected `name` to be a localized string (string).',
          key: 'WizardHeaderWithOverview: missing wizard step name'
        });
      }
    }
    if (stepName && stepName !== prevStepName) {
      stepNames.push(stepName);
      prevStepName = stepName;
    }
  });
  if (stepNames.length > stepLimit) {
    if (process.env.NODE_ENV !== 'production') {
      devLogger.warn({
        message: `WizardHeaderWithOverview: ${stepNames.length} steps given, but the maximum is ${stepLimit}.`,
        key: 'WizardHeaderWithOverview: Too many steps'
      });
    }
  }
  return stepNames;
};
const renderStepIndicator = ({
  stepNames,
  currentStepName,
  inPanel
}) => currentStepName ? /*#__PURE__*/_jsx(StepIndicator, {
  direction: "horizontal",
  stepIndex: stepNames.indexOf(currentStepName),
  stepNames: stepNames,
  use: inPanel ? 'flush' : 'default'
}) : '';
const StepCountDiv = styled.div.withConfig({
  displayName: "WizardHeaderWithOverview__StepCountDiv",
  componentId: "ckkron-0"
})(({
  theme
}) => {
  return {
    marginLeft: theme.space['300']
  };
});
const renderStepCount = ({
  stepNames,
  currentStepName
}) => {
  if (!currentStepName) {
    return null;
  }
  const stepNumber = stepNames.indexOf(currentStepName) + 1;
  const stepCount = stepNames.length;
  return /*#__PURE__*/_jsx(StepCountDiv, {
    children: unescapedText('salesUI.UIWizard.stepCount', {
      stepNumber,
      stepCount
    })
  });
};
const StepHeadingSpan = styled.span.withConfig({
  displayName: "WizardHeaderWithOverview__StepHeadingSpan",
  componentId: "ckkron-1"
})(({
  theme
}) => {
  return {
    marginRight: theme.space['300']
  };
});
export const WizardHeaderWithOverview = ({
  stepProps,
  wizardProps,
  headerdatatestid
}) => {
  const theme = useTheme();
  const {
    inPanel
  } = usePanelContext();
  const stepHeading = stepProps.heading;
  const defaultHeading = wizardProps.defaultHeading;
  const stepNames = getStepNames({
    wizardProps,
    inPanel
  });
  const currentStepName = stepProps && stepProps.name;
  return /*#__PURE__*/_jsx(WizardHeaderProvider, {
    sectionStyleOverrides: {
      backgroundColor: theme.color['fill-surface-default']
    },
    stepIndicatorStyleOverrides: {
      fontWeight: inPanel ? theme.typography['detail-100'].fontWeight : theme.typography['heading-25'].fontWeight
    },
    children: inPanel ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: wizardProps.onReject,
        "data-wizard-action-name": "close"
      }), /*#__PURE__*/_jsx(DialogHeader, {
        "data-test-id": headerdatatestid,
        children: /*#__PURE__*/_jsx(Text, {
          as: "h4",
          children: wizardProps.defaultHeading
        })
      }), /*#__PURE__*/_jsx(PanelSection, {
        marginBlockEnd: "0",
        paddingBlockStart: "500",
        children: /*#__PURE__*/_jsx(Flex, {
          children: renderStepIndicator({
            stepNames,
            currentStepName,
            inPanel
          })
        })
      })]
    }) : /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      paddingInline: "1000",
      paddingBlock: "300",
      "data-test-id": headerdatatestid,
      backgroundColor: "fill-surface-default",
      children: [/*#__PURE__*/_jsx(StepHeadingSpan, {
        "aria-level": 2,
        role: "heading",
        children: stepHeading || defaultHeading
      }), renderStepIndicator({
        stepNames,
        currentStepName,
        inPanel
      }), currentStepName && renderStepCount({
        stepNames,
        currentStepName
      })]
    })
  });
};
WizardHeaderWithOverview.displayName = 'WizardHeaderWithOverview';